/**
 * Price Change Calculator
 *
 * Uses the probability-weighted expected delta from price-change-engine.ts,
 * matching exactly what the Budget Boost table shows.
 */

import {
  applyPriceFloorToTierBProbabilities,
  determineTier,
  type SeasonPricing,
} from "../constants";
import {
  calculateExpectedPriceDelta,
  calculatePriceChangeProbabilities,
} from "../price-change-engine";

/**
 * Determine price tier based on current price and the season's threshold.
 * Uses the shared determineTier from constants.ts
 */
export const getTier = determineTier;

/**
 * Calculate expected price change based on predicted points and tier.
 *
 * Uses probability-weighted expected delta — the same system as Budget Boost.
 * Returns a continuous value (e.g. 0.22 for 60% +0.3 / 40% +0.1) rather than
 * a discrete bucket, giving more accurate price change predictions.
 */
export function calculateSimplePriceChange(
  predictedPoints: number,
  tier: "A" | "B",
  currentPrice: number,
  priceFloor: number,
): number {
  const rawProbs = calculatePriceChangeProbabilities(predictedPoints, tier);
  const probs =
    tier === "B"
      ? applyPriceFloorToTierBProbabilities(currentPrice, rawProbs, priceFloor)
      : rawProbs;
  return calculateExpectedPriceDelta(probs);
}

/**
 * Combined helper for algorithms to use.
 *
 * @param pricing - The season's pricing rules (from getSeasonPricing)
 */
export function calculatePriceChange(
  predictedPoints: number,
  currentPrice: number,
  pricing: SeasonPricing,
  tier?: "A" | "B",
): number {
  const resolvedTier = tier ?? getTier(currentPrice, pricing.tierThreshold);
  return calculateSimplePriceChange(
    predictedPoints,
    resolvedTier,
    currentPrice,
    pricing.priceFloor,
  );
}
